import Constants from 'expo-constants';
import * as Sentry from '@sentry/react-native';
import posthog from 'posthog-js';
import logger from '../utils/logger';
import { trackConversion } from '../utils/gtmHelpers';
import {
  getStoredGclid,
  getStoredGclidCapturedAt,
  hasAdvertisementConsent,
} from '../utils/posthogAttribution';

/**
 * Paddle Integration Library
 *
 * This module handles Paddle Billing integration for the CarDoo app.
 * It provides functions to initialize Paddle.js and open checkout overlays.
 *
 * Features:
 * - Lazy loading of Paddle.js script (only loads when needed)
 * - Environment-aware configuration (sandbox/production)
 * - Custom data routing for Flow A (authenticated) and Flow B (anonymous)
 * - Success callbacks for post-payment actions
 *
 * Usage:
 * ```js
 * import { openCheckout, PADDLE_CONFIG } from './lib/paddle';
 *
 * // For authenticated users (Flow A)
 * await openCheckout({
 *   priceId: PADDLE_CONFIG.priceIds.pro,
 *   userId: user.id,
 *   email: user.email,
 * });
 *
 * // For anonymous users (Flow B)
 * await openCheckout({
 *   priceId: PADDLE_CONFIG.priceIds.pro,
 *   token: purchaseSessionId,
 *   email: 'user@example.com',
 * });
 * ```
 */

// ============================================================================
// Configuration
// ============================================================================

/**
 * Get Paddle configuration from environment variables
 */
function getPaddleConfig() {
  try {
    const expoConstants = Constants.expoConfig?.extra;

    if (process.env.JEST_WORKER_ID) {
      // Jest test environment
      const proPriceId = process.env.EXPO_PUBLIC_PADDLE_PRO_PRICE_ID;
      return {
        environment: process.env.EXPO_PUBLIC_PADDLE_ENV || 'sandbox',
        clientToken: process.env.EXPO_PUBLIC_PADDLE_CLIENT_TOKEN,
        priceIds: {
          pro: proPriceId,
          proMonthly: proPriceId, // Step 5 alias
          // Step 8 (inline-transaction trial): the deferred trial price is now
          // minted per-checkout inline by the create-trial-checkout edge
          // function, so there is no fixed catalog "trialing" price id here.
          proAnnual: process.env.EXPO_PUBLIC_PADDLE_PRO_ANNUAL_PRICE_ID,
          carBuyersPass: process.env.EXPO_PUBLIC_PADDLE_CBP_PRICE_ID,
          premium: process.env.EXPO_PUBLIC_PADDLE_PREMIUM_PRICE_ID,
        },
        discountCode: process.env.EXPO_PUBLIC_PADDLE_DISCOUNT_CODE,
        frDiscountIds: {
          monthly: process.env.EXPO_PUBLIC_PADDLE_FR_MONTHLY_DISCOUNT_ID,
          annual: process.env.EXPO_PUBLIC_PADDLE_FR_ANNUAL_DISCOUNT_ID,
          cbp: process.env.EXPO_PUBLIC_PADDLE_FR_CBP_DISCOUNT_ID,
        },
        successUrl: process.env.APP_FRONTEND_BASE_URL + '/auth?view=signup',
        appUrl: process.env.APP_FRONTEND_BASE_URL,
      };
    }

    // Check if Paddle config exists in expo constants, otherwise fall back to process.env
    const hasPaddleInExpoConfig = expoConstants?.paddleClientToken || expoConstants?.paddleProPriceId;

    if (expoConstants && hasPaddleInExpoConfig) {
      const proPriceId = expoConstants.paddleProPriceId;
      return {
        environment: expoConstants.paddleEnv || 'sandbox',
        clientToken: expoConstants.paddleClientToken,
        priceIds: {
          pro: proPriceId,
          proMonthly: expoConstants.paddleProMonthlyPriceId || proPriceId, // Step 5 alias
          // Step 8 (inline-transaction trial): the deferred trial price is now
          // minted per-checkout inline by the create-trial-checkout edge
          // function, so there is no fixed catalog "trialing" price id here.
          proAnnual: expoConstants.paddleProAnnualPriceId,
          carBuyersPass: expoConstants.paddleCarBuyersPassPriceId,
          premium: expoConstants.paddlePremiumPriceId,
        },
        discountCode: expoConstants.paddleDiscountCode || process.env.EXPO_PUBLIC_PADDLE_DISCOUNT_CODE,
        frDiscountIds: {
          monthly: expoConstants.paddleFrMonthlyDiscountId || process.env.EXPO_PUBLIC_PADDLE_FR_MONTHLY_DISCOUNT_ID,
          annual: expoConstants.paddleFrAnnualDiscountId || process.env.EXPO_PUBLIC_PADDLE_FR_ANNUAL_DISCOUNT_ID,
          cbp: expoConstants.paddleFrCbpDiscountId || process.env.EXPO_PUBLIC_PADDLE_FR_CBP_DISCOUNT_ID,
        },
        successUrl: (expoConstants.appFrontendBaseUrl || 'https://dev.doocars.com') + '/auth?view=signup',
        appUrl: expoConstants.appFrontendBaseUrl || 'https://dev.doocars.com',
      };
    }

    // Fallback to process.env (web environment)
    const proPriceId = process.env.EXPO_PUBLIC_PADDLE_PRO_PRICE_ID;
    return {
      environment: process.env.EXPO_PUBLIC_PADDLE_ENV || 'sandbox',
      clientToken: process.env.EXPO_PUBLIC_PADDLE_CLIENT_TOKEN,
      priceIds: {
        pro: proPriceId,
        proMonthly: proPriceId,
        // Step 8 (inline-transaction trial): the deferred trial price is now
        // minted per-checkout inline by the create-trial-checkout edge
        // function, so there is no fixed catalog "trialing" price id here.
        proAnnual: process.env.EXPO_PUBLIC_PADDLE_PRO_ANNUAL_PRICE_ID,
        carBuyersPass: process.env.EXPO_PUBLIC_PADDLE_CBP_PRICE_ID,
        premium: process.env.EXPO_PUBLIC_PADDLE_PREMIUM_PRICE_ID,
      },
      discountCode: process.env.EXPO_PUBLIC_PADDLE_DISCOUNT_CODE,
      frDiscountIds: {
        monthly: process.env.EXPO_PUBLIC_PADDLE_FR_MONTHLY_DISCOUNT_ID,
        annual: process.env.EXPO_PUBLIC_PADDLE_FR_ANNUAL_DISCOUNT_ID,
        cbp: process.env.EXPO_PUBLIC_PADDLE_FR_CBP_DISCOUNT_ID,
      },
      successUrl: (process.env.APP_FRONTEND_BASE_URL || 'https://dev.doocars.com') + '/auth?view=signup',
      appUrl: process.env.APP_FRONTEND_BASE_URL || 'https://dev.doocars.com',
    };
  } catch (error) {
    logger.error('[Paddle] Error loading configuration:', error);
    Sentry.captureException(error, {
      tags: { component: 'paddle-config' },
    });

    // Return safe defaults
    return {
      environment: 'sandbox',
      clientToken: null,
      priceIds: {
        pro: null,
        premium: null,
      },
      discountCode: null,
      frDiscountIds: { monthly: null, annual: null, cbp: null },
      successUrl: 'https://dev.doocars.com/auth?view=signup',
      appUrl: 'https://dev.doocars.com',
    };
  }
}

export const PADDLE_CONFIG = getPaddleConfig();

// Track initialization state
let paddleInitialized = false;
let paddleInitializing = false;
let initPromise = null;

// Store current checkout callbacks (to avoid race condition)
// These are set BEFORE opening checkout, so the event handler can reference them
let currentOnSuccess = null;
let currentOnError = null;
let currentOnClose = null;

// ============================================================================
// Paddle Initialization
// ============================================================================

/**
 * Initialize Paddle.js by loading the script and calling Paddle.Initialize()
 *
 * This function is idempotent - subsequent calls return immediately if already initialized.
 * Handles race conditions by returning the same promise if initialization is in progress.
 *
 * @returns {Promise<void>}
 * @throws {Error} If Paddle.js fails to load or initialize
 */
export function initPaddle() {
  // Return immediately if already initialized
  if (paddleInitialized && typeof window !== 'undefined' && window.Paddle) {
    logger.log('[Paddle] Already initialized');
    return Promise.resolve();
  }

  // Return existing initialization promise if in progress
  if (paddleInitializing && initPromise) {
    logger.log('[Paddle] Initialization already in progress');
    return initPromise;
  }

  paddleInitializing = true;

  initPromise = new Promise((resolve, reject) => {
    // Check if running in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      logger.warn('[Paddle] Not in browser environment');
      paddleInitializing = false;
      reject(new Error('Paddle.js requires a browser environment'));
      return;
    }

    // Validate configuration
    if (!PADDLE_CONFIG.clientToken) {
      logger.error('[Paddle] Missing client token');
      paddleInitializing = false;
      reject(new Error('Paddle client token not configured'));
      return;
    }

    logger.log('[Paddle] Initializing Paddle.js...');

    // Create script element
    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;

    script.onload = () => {
      try {
        logger.log('[Paddle] Script loaded, initializing Paddle...');

        // Explicitly set environment before initialization
        // This is required to avoid JWT retrieval errors with client-side tokens
        if (PADDLE_CONFIG.environment === 'sandbox') {
          window.Paddle.Environment.set('sandbox');
          logger.log('[Paddle] Environment explicitly set to sandbox');
        }

        // Initialize Paddle with client token and global event callback
        // IMPORTANT: Register eventCallback during init to avoid race condition
        // where checkout.completed fires before callback is registered
        window.Paddle.Initialize({
          token: PADDLE_CONFIG.clientToken,
          eventCallback: (data) => {
            if (data.name === 'checkout.completed') {
              logger.log('[Paddle] Checkout completed:', data);
              Sentry.addBreadcrumb({
                category: 'paddle',
                message: 'Checkout completed',
                data: { transactionId: data?.data?.transaction_id },
                level: 'info',
              });

              // Track conversion to GTM/Google Ads
              trackPurchaseConversion(data.data);

              // Dismiss Paddle's built-in "Your transaction has been completed
              // successfully..." screen. Our success callback owns the post-purchase
              // UX (modal dismissal, /home redirect, ProActivationToast via the
              // Realtime UPDATE), so Paddle's thank-you screen is redundant.
              try { window.Paddle?.Checkout?.close?.(); } catch (_) { /* noop */ }

              // Call stored success callback (set before checkout opened)
              if (currentOnSuccess) {
                const successCallback = currentOnSuccess;
                // Clear callbacks after use
                currentOnSuccess = null;
                currentOnError = null;
                currentOnClose = null;
                successCallback(data.data);
              }
            } else if (data.name === 'checkout.error') {
              // Check if this is a user validation error (e.g., missing ZIP code)
              // These are expected user input issues, not application errors
              const isValidationError = data?.code === 'validation' || data?.type === 'front-end_error';

              if (isValidationError) {
                logger.warn('[Paddle] Checkout validation error:', data?.detail || data);
                Sentry.addBreadcrumb({
                  category: 'paddle',
                  message: 'Checkout validation error',
                  data: { detail: data?.detail, code: data?.code },
                  level: 'warning',
                });
              } else {
                // Capture actual system/payment errors to Sentry
                logger.error('[Paddle] Checkout error:', data);
                Sentry.captureException(new Error('Paddle checkout error'), {
                  tags: { component: 'paddle-checkout' },
                  extra: { error: data },
                });
              }

              if (currentOnError) {
                const errorCallback = currentOnError;
                // Clear callbacks after use
                currentOnSuccess = null;
                currentOnError = null;
                currentOnClose = null;
                errorCallback(data);
              }
            } else if (data.name === 'checkout.payment.failed') {
              logger.warn('[Paddle] Payment failed:', data);

              // Track to PostHog
              posthog.capture('paywall_payment_failed', {
                transaction_id: data?.data?.transaction_id,
                error_code: data?.data?.payment?.error_code,
                payment_method: data?.data?.payment?.method_details?.type,
                currency: data?.data?.currency_code,
              });

              Sentry.addBreadcrumb({
                category: 'paddle',
                message: 'Payment failed',
                data: {
                  transactionId: data?.data?.transaction_id,
                  errorCode: data?.data?.payment?.error_code,
                },
                level: 'warning',
              });

              // Also trigger the onError callback if set
              if (currentOnError) {
                currentOnError(data);
              }
            } else if (data.name === 'checkout.closed') {
              logger.log('[Paddle] Checkout closed by user');

              // Track checkout abandonment to PostHog
              posthog.capture('paywall_checkout_abandoned', {
                transaction_id: data?.data?.transaction_id,
                had_items: !!data?.data?.items?.length,
              });

              // Notify the caller so it can reset transient UI state
              // (spinners, disabled buttons). Fired only when the modal closes
              // without success/error already firing.
              const closeCallback = currentOnClose;
              currentOnSuccess = null;
              currentOnError = null;
              currentOnClose = null;
              if (closeCallback) closeCallback(data);
            }
          },
        });

        paddleInitialized = true;
        paddleInitializing = false;

        logger.log(`[Paddle] Initialized successfully (${PADDLE_CONFIG.environment} mode)`);

        Sentry.addBreadcrumb({
          category: 'paddle',
          message: 'Paddle.js initialized',
          data: {
            environment: PADDLE_CONFIG.environment,
          },
          level: 'info',
        });

        resolve();
      } catch (error) {
        paddleInitializing = false;
        logger.error('[Paddle] Initialization failed:', error);
        Sentry.captureException(error, {
          tags: { component: 'paddle-init' },
        });
        reject(error);
      }
    };

    script.onerror = () => {
      paddleInitializing = false;
      const error = new Error('Failed to load Paddle.js script');
      logger.error('[Paddle]', error.message);
      Sentry.captureException(error, {
        tags: { component: 'paddle-script' },
      });
      reject(error);
    };

    document.body.appendChild(script);
  });

  return initPromise;
}

// ============================================================================
// Conversion Tracking
// ============================================================================

/**
 * Track purchase conversion to GTM/Google Ads
 *
 * Extracts value, currency, transaction type, item, and customer data from
 * Paddle checkout.completed event and sends a conversion event with enhanced
 * conversions support (email + address fields where available).
 *
 * @param {Object} checkoutData - Paddle checkout.completed event data
 */
function trackPurchaseConversion(checkoutData) {
  try {
    const grandTotal = checkoutData?.totals?.grand_total || checkoutData?.totals?.total || '0';
    const value = parseFloat(grandTotal) || 0;
    const currency = checkoutData?.currency_code || 'EUR';
    const transactionId = checkoutData?.transaction_id || checkoutData?.id || null;

    const subscriptionId = checkoutData?.subscription_id || null;
    const transactionType = subscriptionId ? 'subscription' : 'one_time';

    const firstItem = checkoutData?.items?.[0];
    const itemId = firstItem?.price_id || firstItem?.price?.id || null;

    const customData = checkoutData?.custom_data || {};
    const itemName = customData?.pricing_tier_selected || null;
    const upgradeSource = customData?.upgrade_source || null;

    const customer = checkoutData?.customer || {};
    const email = customer?.email || null;
    const address = checkoutData?.address || customer?.address || {};
    const userData = {};
    if (email) userData.email = email;
    if (customer?.first_name) userData.firstName = customer.first_name;
    if (customer?.last_name) userData.lastName = customer.last_name;
    if (address?.postal_code) userData.postalCode = address.postal_code;
    if (address?.country_code) userData.country = address.country_code;
    if (address?.city) userData.city = address.city;
    if (address?.region) userData.region = address.region;

    logger.log('[Paddle] Tracking purchase conversion:', {
      value,
      currency,
      transactionId,
      transactionType,
      itemId,
      itemName,
      upgradeSource,
      hasEmail: !!email,
      userDataKeys: Object.keys(userData),
    });

    trackConversion(
      'purchase_complete',
      {
        transaction_id: transactionId,
        value,
        currency,
        transaction_type: transactionType,
        item_id: itemId,
        item_name: itemName,
        upgrade_source: upgradeSource,
        subscription_id: subscriptionId,
      },
      Object.keys(userData).length > 0 ? userData : null
    );

    Sentry.addBreadcrumb({
      category: 'conversion',
      message: 'Purchase conversion tracked',
      data: { value, currency, transactionId, transactionType, itemId },
      level: 'info',
    });
  } catch (error) {
    logger.error('[Paddle] Failed to track purchase conversion:', error);
    Sentry.captureException(error, {
      tags: { component: 'paddle-conversion' },
    });
  }
}

// ============================================================================
// Checkout Functions
// ============================================================================

/**
 * Open Paddle checkout overlay
 *
 * @param {Object} params - Checkout parameters
 * @param {string} params.priceId - Paddle price ID (required)
 * @param {string} [params.userId] - Supabase user ID (Flow A: authenticated)
 * @param {string} [params.email] - User email
 * @param {string} [params.token] - Purchase session token (Flow B: anonymous)
 * @param {Object} [params.extraCustomData] - Additional keys merged into customData
 *   AFTER Flow A/B keys are set (Step 5: { upgrade_source, pricing_tier_selected }).
 *   Lets callers attach analytics data without knowing Flow A vs Flow B internals.
 * @param {string} [params.discountCode] - Optional discount code
 * @param {Function} [params.onSuccess] - Callback fired on successful checkout
 * @param {Function} [params.onError] - Callback fired on checkout error
 * @param {Function} [params.onClose] - Callback fired when the user dismisses
 *   the overlay without completing or erroring (use to reset spinners/disabled
 *   state). Not fired when onSuccess/onError already ran.
 *
 * @returns {Promise<void>}
 * @throws {Error} If Paddle initialization fails or invalid parameters
 */
export async function openCheckout({
  priceId,
  userId,
  email,
  token,
  extraCustomData,
  discountCode,
  onSuccess,
  onError,
  onClose,
}) {
  try {
    // Validate required parameters
    if (!priceId) {
      throw new Error('priceId is required');
    }

    // Initialize Paddle (lazy loading)
    await initPaddle();

    logger.log('[Paddle] Opening checkout:', {
      priceId,
      hasUserId: !!userId,
      hasEmail: !!email,
      hasToken: !!token,
      flow: userId ? 'A' : 'B',
    });

    // Build custom data for webhook routing
    const customData = {};

    if (userId) {
      // Flow A: Authenticated user
      customData.supabase_user_id = userId;
    }

    if (token) {
      // Flow B: Anonymous purchase
      customData.purchase_session_id = token;
    }

    // Task 24: Flow B GCLID propagation. Anonymous checkouts cannot be
    // attributed via the supabase_user_id path, so we forward the stored
    // GCLID + its captured_at timestamp via Paddle customData. The webhook
    // handler reads these out of paddle.event.data.custom_data and writes
    // them to the conversion record. Gated on advertisement consent
    // (Requirements 2.1, 2.2, 5.6) and only relevant when userId is absent
    // and token (purchase_session_id) is present.
    if (!userId && token && hasAdvertisementConsent()) {
      const storedGclid = await getStoredGclid();
      const storedCapturedAt = await getStoredGclidCapturedAt();
      if (storedGclid) {
        customData.attribution_gclid = storedGclid;
        if (storedCapturedAt) {
          customData.attribution_gclid_captured_at = storedCapturedAt;
        }
      }
    }

    // Step 5: merge caller-supplied analytics data AFTER Flow A/B keys
    if (extraCustomData && typeof extraCustomData === 'object') {
      Object.assign(customData, extraCustomData);
    }

    // Build checkout configuration
    const checkoutConfig = {
      items: [{ priceId, quantity: 1 }],
      customData,
      settings: {
        displayMode: 'overlay',
        theme: 'light',
        locale: 'en',
        showAddDiscounts: true, // Allow users to enter discount codes
      },
    };

    // Add customer email if provided
    if (email) {
      checkoutConfig.customer = { email };
    }

    // Add discount if provided (supports both code and ID)
    if (discountCode) {
      // If it starts with 'dsc_', it's a discount ID, otherwise it's a code
      if (discountCode.startsWith('dsc_')) {
        checkoutConfig.discountId = discountCode;
      } else {
        checkoutConfig.discountCode = discountCode;
      }
    }

    // For anonymous users, set success URL with token for claim flow
    if (token && !userId) {
      checkoutConfig.settings.successUrl = `${PADDLE_CONFIG.successUrl}&ref_id=${token}`;
      logger.log('[Paddle] Success URL:', checkoutConfig.settings.successUrl);
    }

    // IMPORTANT: Set callbacks BEFORE opening checkout to avoid race condition
    // The global eventCallback (registered during init) will use these
    currentOnSuccess = onSuccess;
    currentOnError = onError;
    currentOnClose = onClose;

    // Open checkout overlay
    window.Paddle.Checkout.open(checkoutConfig);

    logger.log('[Paddle] Checkout opened');

    Sentry.addBreadcrumb({
      category: 'paddle',
      message: 'Checkout opened',
      data: {
        priceId,
        flow: userId ? 'A' : 'B',
        hasEmail: !!email,
      },
      level: 'info',
    });
  } catch (error) {
    logger.error('[Paddle] Failed to open checkout:', error);

    Sentry.captureException(error, {
      tags: { component: 'paddle-checkout' },
      extra: {
        priceId,
        userId,
        hasEmail: !!email,
        hasToken: !!token,
      },
    });

    if (onError) {
      onError(error);
    }

    throw error;
  }
}

/**
 * D-030: open Paddle's overlay against an existing transaction (typically a
 * past-due renewal) so the user can update the payment method and Paddle
 * retries the charge in one flow. On success Paddle fires
 * `transaction.completed` → `subscription.resumed`; TierContext then flips
 * status back to `active` and the past-due banner auto-dismisses via D-029.
 *
 * Differs from `openCheckout` in that it does not pass a price id or items —
 * the transaction already exists and owns those details. Paddle's Checkout
 * overlay renders as a "retry this outstanding payment" flow.
 *
 * Callers should fall back to the portal/mailto path if this throws or if
 * Paddle.js fails to load (e.g. script blocked).
 *
 * @param {Object} params
 * @param {string} params.transactionId - Paddle transaction id (e.g. `txn_01h...`)
 * @param {string} [params.email]       - Prefill email to skip the customer step
 * @param {Function} [params.onSuccess]
 * @param {Function} [params.onError]
 */
export async function openUpdatePaymentCheckout({
  transactionId,
  email,
  onSuccess,
  onError,
}) {
  if (!transactionId) throw new Error('transactionId is required');
  await initPaddle();

  const checkoutConfig = {
    transactionId,
    settings: {
      displayMode: 'overlay',
      theme: 'light',
      locale: 'en',
    },
  };
  if (email) checkoutConfig.customer = { email };

  currentOnSuccess = onSuccess;
  currentOnError = onError;
  currentOnClose = null;

  window.Paddle.Checkout.open(checkoutConfig);

  logger.log('[Paddle] Update-payment checkout opened for', transactionId);
  Sentry.addBreadcrumb({
    category: 'paddle',
    message: 'Update-payment checkout opened',
    data: { transactionId, hasEmail: !!email },
    level: 'info',
  });
}

/**
 * Step 8 (inline-transaction trial): open Paddle's overlay against a deferred-
 * trial TRANSACTION created server-side by the create-trial-checkout edge
 * function. The transaction already carries the inline price (with the correct
 * per-user trial_period), the welcome discount, and the custom_data markers, so
 * we only pass the transaction id. Modeled on openUpdatePaymentCheckout but WITH
 * onClose support so TrialUnlockModal can reset its spinner when the user
 * dismisses the overlay.
 *
 * @param {Object} params
 * @param {string} params.transactionId - Paddle transaction id (e.g. `txn_01h...`)
 * @param {string} [params.email]       - Prefill email to skip the customer step
 * @param {Object} [params.customData]  - COMPLETE custom_data to attach to the
 *   checkout. Passing customData to Checkout.open OVERWRITES the transaction's
 *   own custom_data, so callers MUST pass the full marker set (including
 *   supabase_user_id, which the webhook requires to flip the user to
 *   trial_carded). If omitted, we pass no customData and rely on the
 *   transaction's own custom_data set server-side.
 * @param {Function} [params.onSuccess]
 * @param {Function} [params.onError]
 * @param {Function} [params.onClose]   - Fired when the user dismisses the overlay
 */
export async function openDeferredTrialCheckout({
  transactionId,
  email,
  customData,
  onSuccess,
  onError,
  onClose,
}) {
  if (!transactionId) throw new Error('transactionId is required');
  await initPaddle();

  const checkoutConfig = {
    transactionId,
    settings: {
      displayMode: 'overlay',
      theme: 'light',
      locale: 'en',
    },
  };
  // Only attach customData when the caller supplies a COMPLETE marker set.
  // Passing customData to Checkout.open overwrites the transaction's own
  // custom_data, so a partial object (missing supabase_user_id) would silently
  // break the webhook's trial_carded flip. When omitted, rely on the
  // transaction's server-set custom_data.
  if (customData && typeof customData === 'object') {
    checkoutConfig.customData = customData;
  }
  if (email) checkoutConfig.customer = { email };

  currentOnSuccess = onSuccess;
  currentOnError = onError;
  currentOnClose = onClose;

  window.Paddle.Checkout.open(checkoutConfig);

  logger.log('[Paddle] Deferred-trial checkout opened for', transactionId);
  Sentry.addBreadcrumb({
    category: 'paddle',
    message: 'Deferred-trial checkout opened',
    data: { transactionId, hasEmail: !!email },
    level: 'info',
  });
}

/**
 * Close Paddle checkout overlay (if open)
 */
export function closeCheckout() {
  if (typeof window !== 'undefined' && window.Paddle && paddleInitialized) {
    try {
      window.Paddle.Checkout.close();
      logger.log('[Paddle] Checkout closed');
    } catch (error) {
      logger.error('[Paddle] Failed to close checkout:', error);
    }
  }
}

/**
 * Check if Paddle is initialized
 *
 * @returns {boolean}
 */
export function isPaddleInitialized() {
  return paddleInitialized && typeof window !== 'undefined' && !!window.Paddle;
}

export default {
  PADDLE_CONFIG,
  initPaddle,
  openCheckout,
  openDeferredTrialCheckout,
  closeCheckout,
  isPaddleInitialized,
};
